/**
 * Parts intents: the stock position (now by site, and below-minimum), what the
 * next 30 days need that is not on the shelf, where one part sits across the
 * network, and which parts are holding a job up.
 *
 * `\bparts?\b` replaces the old `part` substring, which matched "department"
 * and "particular".
 */

import { BY_ID, MINES } from '../../../data/core.ts';
import type { MineCode } from '../../../schema/machine.ts';
import { predictedUsage } from '../../oem-engine.ts';
import { loadOemDomain } from '../../useOemData.ts';
import { route } from '../answer-kit.ts';
import { groupNum } from '../stats.ts';
import { TOOLS, loadWorkOrders, mineLocations } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

/** The regex engine's stock keyword ladder, kept so old answers stay put. */
function stockKeyword(L: string, fallback?: string): string | undefined {
  return /coupling/.test(L)
    ? 'coupling'
    : /brake|pad/.test(L)
      ? 'brake'
      : /front|rock drill/.test(L)
        ? 'front-end'
        : /tyre/.test(L)
          ? 'tyre'
          : fallback;
}

// ---------------------------------------------------------------------------
// stock_check — demo L4077
// ---------------------------------------------------------------------------

export const stockCheck: IntentDef = {
  id: 'stock_check',
  family: 'parts',
  priority: 50,
  examples: [
    'Do we have brake pads for T-09 on site?',
    'Which parts are below minimum at the Broken Ridge store?',
    'Do we have a rotary head motor for S-09 in store?',
  ],
  score: cues({
    any: [
      /\bstock/,
      /\bparts?\b/,
      /\bkits?\b/,
      /\bspares?\b/,
      /lead time/,
      /\bpads?\b/,
      /\btyres?\b/,
      /\bfilters?\b/,
      /coupling/,
      /on site|in store|in the store|\bstores?\b|on the shelf|on hand/,
      /do we have/,
      /below (min|minimum)|under min/,
    ],
    penalty: [[/\bworth\b|justif|air freight/, 3]],
  }),
  async answer({ kit, E, L }) {
    const belowMin = /below (min|minimum)|under min|short of min/.test(L);
    // "for T-09 on site" means T-09's site store and the DC that serves it.
    const mine: MineCode | null =
      E.mine ?? (E.machine && !belowMin && /on site|in store|in the store/.test(L) ? BY_ID[E.machine].mine : null);
    const keyword = stockKeyword(L, E.components[0]?.stock);
    const s = await kit.run(TOOLS.stock_check, {
      keyword,
      machine_id: E.machine ?? null,
      mine,
      below_min: belowMin,
    });
    const where = mine ? `${MINES[mine].name} site store` : null;
    if (belowMin) {
      const markdown =
        `**Below minimum${where ? ` · ${where}` : ' · every site store'}** — ${s.length} part line${s.length === 1 ? '' : 's'}` +
        (mine ? ` (with ${mineLocations(mine)[1]}, the DC that serves it)` : '') +
        `\n\n` +
        (s.length
          ? s
              .slice(0, 8)
              .map(
                (p) =>
                  `- **${p.part}** ${p.description} — A$${groupNum(p.list_price_aud)}, ${p.lead_time_days} d, ` +
                  `${p.criticality}\n  ${p.stock.join(' · ')}`,
              )
              .join('\n')
          : '- Nothing below minimum.') +
        (s.length > 8 ? `\n\n…and ${s.length - 8} more in the Parts pipeline.` : '') +
        `\n\nThe Inventory Network agent proposes new minimums in Parts pipeline → Predictive usage.`;
      if (mine) kit.focus({ mine });
      return kit.done(markdown, ['stock_check', 'Salesforce inventory'], { navigate: route('partsflow') });
    }
    const markdown =
      `**Stock position${where ? ` · ${where}` : ''}**\n\n` +
      s
        .slice(0, 5)
        .map(
          (p) =>
            `- **${p.part}** ${p.description} — A$${groupNum(p.list_price_aud)}, ${p.lead_time_days} d, ` +
            `${p.criticality}\n  ${p.stock.join(' · ')}`,
        )
        .join('\n');
    if (E.machine) kit.focus({ machine: E.machine, mine: BY_ID[E.machine].mine });
    return kit.done(markdown, ['stock_check', 'Salesforce inventory']);
  },
};

// ---------------------------------------------------------------------------
// predicted_usage — demo L4060
// ---------------------------------------------------------------------------

export const predictedUsageIntent: IntentDef = {
  id: 'predicted_usage',
  family: 'parts',
  priority: 95,
  examples: ['Which parts do we need in the next 30 days that are not on the shelf?'],
  score: cues({
    any: [
      /parts do we need|not on the shelf|predicted (usage|demand)/,
      /\bparts?\b.*next 30 days|next 30 days.*\bparts?\b/,
    ],
    base: 5,
  }),
  async answer({ kit, E }) {
    kit.ran('predicted_usage', { scope: 'uncovered' });
    const parts = await loadOemDomain('parts');
    const pu = predictedUsage({ parts: parts.parts, inventory: parts.inventory }).filter(
      (r) => r.gap > 0 && (!E.mine || r.mine === E.mine),
    );
    const markdown =
      `**${pu.length} part lines** are needed inside a failure window and not covered by the site store: ` +
      `${pu
        .map(
          (r) =>
            `**${r.part_no}** at ${MINES[r.mine].name.split(' ')[0]} (${r.machines.join(', ')}) — ${r.prop}`,
        )
        .join('; ')}. ` +
      `Proposed minimums are in the Parts pipeline → Predictive usage tab.`;
    return kit.done(
      markdown,
      ['component_health.csv', 'inventory_snapshot.csv', 'parts_catalogue.csv'],
      { navigate: route('partsflow', 'predict') },
    );
  },
};

// ---------------------------------------------------------------------------
// part_network — new: "is the rotary head motor for S-09 the only one?"
// ---------------------------------------------------------------------------

export const partNetwork: IntentDef = {
  id: 'part_network',
  family: 'parts',
  priority: 82,
  examples: ['Is the rotary head motor for S-09 the only one in the network?', 'Where else can we get a front-end kit?'],
  score: cues({
    any: [
      /only one|the last one|anywhere else/,
      /(in|across) the (whole )?(network|supply chain)/,
      /other (stores|locations|dcs|sites|warehouses)/,
      /where (else )?(can|could) (we|i) (get|find|source)/,
      /which (dc|store|warehouse|location)s? (has|have|holds?)/,
    ],
    base: 6,
  }),
  async answer({ kit, E, q }) {
    const phrase = E.partNos[0]
      ? null
      : q
          .replace(/\b[JLPTS]-\d\d\b/gi, ' ')
          .replace(/\b(is|the|for|only|one|in|network|a|an|of|where|else|can|we|get|any|other|which|has|have|there|last|whole|supply|chain|find|source|could|i)\b/gi, ' ')
          .replace(/[^\w\s-]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
    const n = await kit.run(TOOLS.part_network, E.partNos[0] ? { part_no: E.partNos[0] } : { keyword: phrase });
    if (!n) {
      return kit.done(
        `I couldn't match that to a catalogue part. Name the part number (e.g. MER-ROT-HD-MOT) or the part ("rotary head motor").`,
        ['parts_catalogue.csv'],
      );
    }
    const holding = n.holding.map((l) => `${l.location} ${l.qty}${l.below ? ` (min ${l.min}, below)` : ''}`).join(' · ');
    const only = n.on_hand === 1;
    const markdown =
      `**${n.part}** ${n.description} — **${n.on_hand} on hand across the network**` +
      `${n.holding.length ? `: ${holding}` : ''}. ` +
      (only
        ? `So yes — that is the only one.`
        : n.on_hand === 0
          ? `None anywhere — it is a ${n.lead_time_days}-day factory lead time.`
          : `So no — there are ${n.on_hand}.`) +
      `\n\n- List A$${groupNum(n.list_price_aud)}, lead time ${n.lead_time_days} d, ${n.criticality}.\n` +
      (n.exchange ? `- Exchange unit in play: ${n.exchange.where} — ${n.exchange.note}.\n` : '') +
      (n.orders.length ? `- In flight: ${n.orders.join('; ')}.` : '');
    if (E.machine) kit.focus({ machine: E.machine, mine: BY_ID[E.machine].mine });
    return kit.done(markdown.trim(), ['part_network', 'Salesforce inventory', 'order_history.csv'], {
      navigate: route('partsflow'),
    });
  },
};

// ---------------------------------------------------------------------------
// parts_holdup — new: "what parts are holding up the Broken Ridge job?"
// ---------------------------------------------------------------------------

export const partsHoldup: IntentDef = {
  id: 'parts_holdup',
  family: 'parts',
  priority: 83,
  examples: ['What parts are holding up the Broken Ridge job?', 'Which jobs are waiting on parts?'],
  score: cues({
    any: [/holding (up|back)|held up|waiting (on|for) parts|awaiting parts|blocked (on|by) parts|parts (delay|hold)/],
    base: 6,
  }),
  async answer({ kit, E }) {
    const [workOrders, parts] = await Promise.all([loadWorkOrders(), loadOemDomain('parts')]);
    kit.ran('parts_holdups', { mine: E.mine });
    const inMine = (id: string) => !E.mine || BY_ID[id]?.mine === E.mine;
    const released = workOrders.filter((w) => w.status.startsWith('Released') && inMine(w.machine_id));
    const pending = parts.orders.filter(
      (o) => /Draft requisition|Ordered/.test(o.status) && inMine(o.machine_id),
    );
    const drafts = workOrders.filter((w) => w.status.startsWith('Draft') && inMine(w.machine_id));
    const lines = [
      ...released.map((w) => `- **${w.wo_id}** ${w.machine_id} ${w.component} — ${w.status}`),
      ...pending.map((o) => {
        const wo = drafts.find((w) => w.machine_id === o.machine_id);
        return `- **${o.order_no}** ${o.machine_id} ${o.part_no}${wo ? ` for ${wo.wo_id}` : ''} — ${o.status}`;
      }),
    ];
    const markdown =
      `**Parts holding up work${E.mine ? ` · ${MINES[E.mine].name}` : ''}** — ${released.length} released order${released.length === 1 ? '' : 's'} waiting on parts, ` +
      `${pending.length} requisition${pending.length === 1 ? '' : 's'} in flight.\n\n` +
      (lines.length ? lines.join('\n') : '- Nothing is waiting on parts.') +
      `\n\nDraft requisitions release when approved in Approvals.`;
    return kit.done(markdown, ['work_orders.csv', 'order_history.csv'], { navigate: route('approvals') });
  },
};
